/**
 * Derives *why* the chatbot is or is not answering on a channel, client-side, and summarises the
 * required-bot-texts projection into the "Bot texts & FAQ" card's blocks (design §17.4).
 *
 * The 2026-09-10 design proposed a server-computed `reasons[]`; the shipped
 * `ChatbotAvailability` contract carries none — no `reasons[]`, no `salesMode`, no `botEnabled`, no
 * phone-collection capability (design-chatbot-settings-page.md §6.1 *delta 2026-09-18*). This module
 * is the client-side replacement: a pure function computing the same "what to fix first" ordering
 * from the fields that DID ship, extended *(delta 2026-09-23)* with `botConfiguredKnown` (§17.5).
 *
 * `MAINTENANCE` and `BOT_DISABLED` are not derivable from the contract and are dropped entirely —
 * never returned here.
 */
import type {
  ChatbotAvailability,
  ChatbotChannel,
  ChatbotChannelAvailability,
  ChatbotTemplateResolution,
} from './types';

export type AvailabilityReason =
  | 'CHATBOT_OFF_PLATFORM'
  | 'CHATBOT_OFF_TENANT'
  | 'TOUCHPOINT_OFF'
  | 'BOT_NOT_CONFIGURED';

/**
 * Ordered by what to fix first. A missing capability object (every capability is optional in the
 * schema) is treated as `{ effective: true, lockedByPlatform: false }` — absence must never paint a
 * working bot as off. *(delta 2026-09-23)* `BOT_NOT_CONFIGURED` fires only when the observation is
 * known (`botConfiguredKnown !== false`) — an unknown bot state is never painted as "not connected".
 */
export function availabilityReasons(
  availability: ChatbotAvailability,
  channel: ChatbotChannelAvailability,
): AvailabilityReason[] {
  const chatbot = availability.chatbotCapability ?? { effective: true, lockedByPlatform: false };
  const reasons: AvailabilityReason[] = [];

  if (!chatbot.effective && chatbot.lockedByPlatform) {
    reasons.push('CHATBOT_OFF_PLATFORM');
  } else if (!chatbot.effective) {
    reasons.push('CHATBOT_OFF_TENANT');
  }
  if (!channel.touchpointEnabled) {
    reasons.push('TOUCHPOINT_OFF');
  }
  if (!channel.botConfigured && channel.botConfiguredKnown !== false) {
    reasons.push('BOT_NOT_CONFIGURED');
  }
  return reasons;
}

/** `effective=true` ⇒ "On"; `effective=false ∧ lockedByPlatform` ⇒ "Locked by platform"; else "Off". */
export type CapabilityDisplayState = 'ON' | 'LOCKED' | 'OFF';

export function capabilityDisplayState(
  capability: { effective: boolean; lockedByPlatform?: boolean } | undefined,
): CapabilityDisplayState {
  const resolved = capability ?? { effective: true, lockedByPlatform: false };
  if (resolved.effective) return 'ON';
  return resolved.lockedByPlatform ? 'LOCKED' : 'OFF';
}

// ── Required bot texts card (design §17.4) ────────────────────────────────────────────────

export interface RequiredTextRow {
  key: string;
  locale: string;
  resolution: ChatbotTemplateResolution;
  channels: ChatbotChannel[];
}

export interface RequiredTextsSummary {
  rows: RequiredTextRow[];
  missing: RequiredTextRow[];
  platformWording: RequiredTextRow[];
  fine: RequiredTextRow[];
  /** True when every channel reported an empty `requiredTemplates[]` — the projection is not ready
   * yet (no content-language sync), distinct from "every text is configured". */
  unknown: boolean;
}

const RESOLUTION_ORDER: Record<ChatbotTemplateResolution, number> = {
  EMPTY: 0,
  SYSTEM_DEFAULT: 1,
  ACCEPTED_DEFAULT: 2,
  TENANT: 3,
};

/**
 * Groups `availability.channels[].requiredTemplates[]` by `(key, locale, resolution)`, merging the
 * channel list per row, per the pseudocode of design §17.4. `unknown` is set only when the entire
 * projection is empty (never inferred from a partial list).
 *
 * Sort: `resolution` first (EMPTY, SYSTEM_DEFAULT, ACCEPTED_DEFAULT, TENANT — "what to fix first");
 * then, within the same resolution, the tenant's default content language first; then the hub's own
 * key order (the order keys are first encountered in the response) — never an alphabetical key sort,
 * which would reorder the hub's own presentation and tell nothing about priority.
 */
export function requiredTextsSummary(
  availability: ChatbotAvailability,
  defaultLocale?: string,
): RequiredTextsSummary {
  const byRow = new Map<string, RequiredTextRow & { firstSeenOrder: number }>();
  let anyEntries = false;
  let nextOrder = 0;

  for (const channel of availability.channels) {
    for (const entry of channel.requiredTemplates ?? []) {
      anyEntries = true;
      const rowKey = `${entry.key}\u0000${entry.locale}\u0000${entry.resolution}`;
      const existing = byRow.get(rowKey);
      if (existing) {
        if (!existing.channels.includes(channel.channel)) existing.channels.push(channel.channel);
      } else {
        byRow.set(rowKey, {
          key: entry.key,
          locale: entry.locale,
          resolution: entry.resolution,
          channels: [channel.channel],
          firstSeenOrder: nextOrder++,
        });
      }
    }
  }

  const rows = [...byRow.values()]
    .sort((a, b) => {
      const resolutionDiff = RESOLUTION_ORDER[a.resolution] - RESOLUTION_ORDER[b.resolution];
      if (resolutionDiff !== 0) return resolutionDiff;
      const aIsDefaultLocale = a.locale === defaultLocale;
      const bIsDefaultLocale = b.locale === defaultLocale;
      if (aIsDefaultLocale !== bIsDefaultLocale) return aIsDefaultLocale ? -1 : 1;
      return a.firstSeenOrder - b.firstSeenOrder;
    })
    .map(({ firstSeenOrder: _firstSeenOrder, ...row }) => row);

  return {
    rows,
    missing: rows.filter((r) => r.resolution === 'EMPTY'),
    platformWording: rows.filter((r) => r.resolution === 'SYSTEM_DEFAULT'),
    fine: rows.filter((r) => r.resolution === 'TENANT' || r.resolution === 'ACCEPTED_DEFAULT'),
    unknown: !anyEntries,
  };
}

/** Distinct locale codes among a set of required-text rows, for the "configured in {{languages}}" line. */
export function distinctLocales(rows: RequiredTextRow[]): string[] {
  return [...new Set(rows.map((r) => r.locale))];
}
